import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import WatchIcon from '@mui/icons-material/Watch';
import AddIcon from '@mui/icons-material/Add';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { patient } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';

/**
 * Devices page.
 *
 * Lists connected wearables / home medical devices and their sync status.
 * The "Connect" button starts an OAuth flow per provider (Fitbit, Withings,
 * Dexcom). The backend returns an OAuth URL we redirect to; the OAuth
 * provider redirects back to a Django webhook that completes the link.
 *
 * Revoking a connection also revokes the associated `device_data` consent
 * (handled server-side by `services/remote_monitoring.revoke`).
 */

type DeviceRow = {
  id: number;
  provider: string;
  device_type: string;
  status: string;
  last_sync_at: string | null;
};

const PROVIDERS = [
  { code: 'fitbit',   label: 'Fitbit (activity, heart rate, sleep)' },
  { code: 'withings', label: 'Withings (scale, BP cuff, sleep mat)' },
  { code: 'dexcom',   label: 'Dexcom (continuous glucose monitor)' },
];

const DEVICE_TYPES = [
  { code: 'wearable',  label: 'Wearable (watch / band)' },
  { code: 'scale',     label: 'Scale' },
  { code: 'bp_cuff',   label: 'Blood pressure cuff' },
  { code: 'glucose',   label: 'Glucose monitor' },
];

export function PatientDevicesPage() {
  const q = useApiQuery({
    queryKey: ['patient', 'devices'],
    queryFn:  patient.devices.list,
  });
  const [connectOpen, setConnectOpen] = useState(false);
  const [revokeId, setRevokeId] = useState<number | null>(null);
  const [provider, setProvider] = useState('fitbit');
  const [deviceType, setDeviceType] = useState('wearable');
  const qc = useQueryClient();
  const toast = useToast();

  const connectMut = useApiMutation({
    mutationFn: () => patient.devices.connect(provider, deviceType),
    onSuccess: (data) => {
      // Hand off to OAuth provider
      window.location.assign(data.oauth_url);
    },
    onError: (e) => toast.error(e.message),
  });

  const revokeMut = useApiMutation({
    mutationFn: (id: number) => patient.devices.revoke(id),
    onSuccess: () => {
      toast.success('Device disconnected.');
      qc.invalidateQueries({ queryKey: ['patient', 'devices'] });
      setRevokeId(null);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Connected devices"
        subtitle="Wearables and home devices that share readings with your record."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setConnectOpen(true)}
          >
            Connect device
          </Button>
        }
      />

      <DataState
        query={q as ReturnType<typeof useApiQuery<DeviceRow[]>>}
        empty={{
          title: 'No devices connected',
          description: 'Connect a wearable or home device so its readings flow into your record automatically.',
        }}
      >
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((d) => (
              <Card key={d.id}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 1.5,
                      bgcolor: 'background.subtle', color: 'primary.main',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <WatchIcon />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                          {d.provider} · {d.device_type.replace(/_/g, ' ')}
                        </Typography>
                        <Chip
                          size="small" label={d.status}
                          color={d.status === 'active' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </Stack>
                      {d.last_sync_at && (
                        <Typography variant="caption" color="text.secondary" className="tabular">
                          last sync {formatDateTime(d.last_sync_at)}
                        </Typography>
                      )}
                    </Box>
                    {d.status === 'active' && (
                      <Button color="error" size="small" onClick={() => setRevokeId(d.id)}>
                        Disconnect
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DataState>

      <Dialog open={connectOpen} onClose={() => setConnectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Connect a device</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select label="Provider" value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              {PROVIDERS.map((p) => <MenuItem key={p.code} value={p.code}>{p.label}</MenuItem>)}
            </TextField>
            <TextField
              select label="Device type" value={deviceType}
              onChange={(e) => setDeviceType(e.target.value)}
            >
              {DEVICE_TYPES.map((d) => <MenuItem key={d.code} value={d.code}>{d.label}</MenuItem>)}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              You'll be redirected to the provider's authorization page,
              then back here. We only ask for read access to the data you
              choose to share.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => connectMut.mutate()}
            disabled={connectMut.isPending}
          >
            Continue to {provider}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={revokeId !== null}
        title="Disconnect device?"
        description="The device will stop sending readings. Past observations stay on your record."
        destructive
        confirmLabel="Disconnect"
        onCancel={() => setRevokeId(null)}
        onConfirm={() => revokeId !== null
          ? revokeMut.mutateAsync(revokeId)
          : Promise.resolve()
        }
      />
    </>
  );
}
